/**
 * =============================================================================
 * ALERT API SERVICE
 * =============================================================================
 *
 * This file contains all API calls for the Alert/Todo system.
 *
 * HOW TO INTEGRATE:
 * -----------------
 * 1. Update API_ENDPOINTS in types.ts with your backend URLs
 * 2. Update DEFAULT_FIELD_MAPPING in types.ts if your API uses different keys
 * 3. Uncomment the actual fetch calls and remove dummy data returns
 *
 * EACH FUNCTION DOCUMENTS:
 * - What API endpoint it calls
 * - What parameters it expects
 * - What response it returns
 * - Line numbers for easy navigation
 *
 * STRUCTURE:
 * - Lines 30-100: Utility functions (mappers, helpers)
 * - Lines 105-160: fetchAlertsByCategory() - GET alerts list
 * - Lines 165-220: fetchActionDetails() - GET action details
 * - Lines 225-280: executeAction() - POST execute action
 * - Lines 285-330: snoozeAlert() - POST snooze
 * - Lines 335-380: deleteAlert() - DELETE alert
 * - Lines 385-420: submitIframeEdit() - POST/PUT after iframe edit
 */

import {
    API_ENDPOINTS,
    DEFAULT_FIELD_MAPPING,
    AlertStatus,
    AlertType,
    Priority,
    ActionType,
    type GeneratedBy,
    type AlertItem,
    type AlertArtifactLink,
    type ArtifactOutcome,
    type AlertAction,
    type ActionDetails,
    type AlertListResponse,
    type ActionDetailsResponse,
    type ActionExecuteResponse,
    type SnoozeResponse,
    type DeleteResponse,
    type CategorizedAlerts,
    type FieldMapping,
    type SupportingDataPoint,
    type AlertContextFilters,
    type BreachingEntity,
} from './types';
import { BASE_API_URL, getMerchantId, getAuthToken } from '@api';
import {
    resolveBreachingEntities,
    detectPattern,
    ResponsePattern,
} from './utils/pattern-resolver';

// Re-export for backward compatibility
export { clearMerchantIdCache } from '@api';

// =============================================================================
// API BASE URL
// =============================================================================
// In development: Uses relative URLs, Vite proxy handles routing:
//   - If VITE_USE_PRODUCTION_API=true, proxy routes to api.reparo.co.in
//   - Otherwise, proxy routes to localhost:8080
// In production: Uses the actual production BASE_API_URL
// =============================================================================
const API_BASE = import.meta.env.DEV ? '' : BASE_API_URL;

console.log(`[TodoChat API] Using production API: ${API_BASE}`);

// Empty categorized alerts for error/empty states
const EMPTY_CATEGORIZED_ALERTS: CategorizedAlerts = {
    pending: [],
    done: [],
    snoozed: [],
};

// =============================================================================
// BACKEND STATUS MAPPING
// Maps CheckListItemStatus (backend) → AlertStatus (frontend)
// =============================================================================
const BACKEND_STATUS_MAP: Record<string, AlertStatus> = {
    CREATED: AlertStatus.PENDING,
    SEEN: AlertStatus.IN_PROGRESS,
    COMPLETED: AlertStatus.COMPLETED,
    SNOOZED: AlertStatus.SNOOZED,
    IGNORED: AlertStatus.CANCELLED,
};

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Builds URL by replacing path parameters
 * Uses API_BASE (empty in dev, full URL in prod)
 *
 * DEV: buildUrl('/check-list-items/list') → '/api/v1/check-list-items/list' (goes through Vite proxy)
 * PROD: buildUrl('/check-list-items/list') → 'https://api.reparo.co.in/api/v1/check-list-items/list'
 */
const buildUrl = (
    template: string,
    params: Record<string, string> = {},
): string => {
    // API_BASE is empty in dev (relative URLs go through Vite proxy)
    // API_BASE is https://api.reparo.co.in in prod
    let url = `${API_BASE}${API_ENDPOINTS.baseUrl}${template}`;
    Object.entries(params).forEach(([key, value]) => {
        url = url.replace(`:${key}`, value);
    });
    return url;
};

/**
 * Maps raw CheckListItemDto response to AlertItem
 *
 * BACKEND RESPONSE (CheckListItemDto):
 * {
 *   checklistItemId: "uuid",
 *   merchantId: "uuid",
 *   heading: "Low stock alert for SKU-123",
 *   data: '{"conditions":{"a":{"sourceValue":5,...}}}',  // JSON string
 *   status: "CREATED",
 *   actions: '[{"id":"1","type":"create_po","label":"Create PO"}]',  // JSON string or null
 *   alertActiveFrom: "2024-01-01T00:00:00Z",
 *   alertActiveUntil: "2024-01-15T00:00:00Z",
 *   actionTaken: null,
 *   actionData: null
 * }
 *
 * @param rawData - Raw CheckListItemDto from API response
 * @param mapping - Field mapping configuration
 * @returns Mapped AlertItem for frontend
 */
// Alert type label mapping (fallback when alertLabel is not provided)
const ALERT_TYPE_LABELS: Record<string, string> = {
    SKU_UNDERSTOCK: 'Low Stock',
    SKU_OVERSTOCK: 'Overstock',
    PO_CREATED: 'PO Created',
    GRN_PENDING: 'GRN Pending',
    FORECAST_REVIEW: 'Forecast Review',
    PAYMENT_DUE: 'Payment Due',
    STOCK_EXPIRY: 'Stock Expiry',
    ORDER_DELAY: 'Order Delay',
    EXPECTED_UNDERSTOCK: 'Expected Understock',
    EXPECTED_OVERSTOCK: 'Expected Overstock',
    LEAD_TIME_ADHERENCE: 'Lead Time Breach',
    DISTRIBUTION_CHANNEL_UNDERSTOCK: 'Channel Restock',
    REPLENISHMENT_QUESTION: 'Planning Question',
    SHIPMENT_DISPATCH_OVERDUE: 'Dispatch Overdue',
    SHIPMENT_FORWARDER_MISSING: 'Forwarder Not Booked',
    SHIPMENT_ARRIVAL_OVERDUE: 'Arrival Overdue',
    PAYMENT_DUE_SOON: 'Payment Due',
};

export const mapApiToAlert = (
    rawData: Record<string, unknown>,
    mapping: FieldMapping = DEFAULT_FIELD_MAPPING,
): AlertItem => {
    // Debug: Log raw data to check ID extraction
    const extractedId = rawData[mapping.alertId] as string;
    const extractedStatus = rawData[mapping.status] as string;
    console.log(
        `[mapApiToAlert] Raw ID field "${mapping.alertId}":`,
        extractedId,
        '| Status:',
        extractedStatus,
    );

    // =========================================================================
    // PARSE JSON STRING FIELDS
    // Backend stores 'data' and 'actions' as JSON strings
    // =========================================================================
    let supportingData: SupportingDataPoint[] = [];
    let actions: AlertAction[] = [];
    let priority: Priority = Priority.MEDIUM;
    let contextFilters: AlertContextFilters | undefined;
    let breachingEntities: BreachingEntity[] | undefined;
    let artifact: AlertArtifactLink | undefined;
    let outcome: ArtifactOutcome | undefined;
    let rawSupportingData: Record<string, unknown> | undefined;
    let entityLinkedFields: string[] | undefined;

    // Parse supportingDataGenerator to extract which entity fields trigger step execution.
    // Scans all step params for $ctx.selectedEntity.<field> patterns — no backend logic needed.
    const generatorJson = rawData.supportingDataGenerator as
        | string
        | null
        | undefined;
    if (generatorJson) {
        try {
            const matches = [
                ...generatorJson.matchAll(/\$ctx\.selectedEntity\.(\w+)/g),
            ];
            const fields = [...new Set(matches.map((m) => m[1]))];
            if (fields.length > 0) entityLinkedFields = fields;
        } catch {
            // ignore parse errors
        }
    }

    // Get alertType and alertLabel from the backend response
    const backendAlertType = rawData.alertType as string | null;
    const backendAlertLabel = rawData.alertLabel as string | null;
    const alertType = (backendAlertType as AlertType) || AlertType.CUSTOM;

    // Use alertLabel if provided, otherwise fall back to ALERT_TYPE_LABELS mapping
    const alertTypeLabel = backendAlertLabel
        ? backendAlertLabel
        : backendAlertType
          ? ALERT_TYPE_LABELS[backendAlertType] ||
            backendAlertType.replace(/_/g, ' ')
          : 'Alert';

    // Parse 'data' JSON string → supportingData + breachingEntities + contextFilters
    const dataString = rawData[mapping.supportingData] as string | null;
    if (dataString) {
        try {
            const parsedData = JSON.parse(dataString);

            // =========================================================================
            // EXTRACT AND NORMALIZE BREACHING ENTITIES
            // Uses pattern-resolver to handle different response patterns:
            // - Pattern A (PAGINATED_LIST): Direct L1 primitives
            // - Pattern B (AGGREGATE_WITH_GROUPINGS): Flatten grouping.PRODUCT.key → product_id
            // - Pattern C (ENTITY_WITH_NESTED_GROUPINGS): L1 primitives, preserve groupings for drilldown
            // =========================================================================
            if (
                parsedData.breachingEntities?.a &&
                Array.isArray(parsedData.breachingEntities.a)
            ) {
                const rawEntities = parsedData.breachingEntities.a;
                const pattern = detectPattern(rawEntities);

                // For Pattern B (AGGREGATE_WITH_GROUPINGS), flatten the grouping object
                // so product_id, product_name, etc. appear as L1 fields in the table
                if (pattern === ResponsePattern.AGGREGATE_WITH_GROUPINGS) {
                    breachingEntities = resolveBreachingEntities(
                        rawEntities,
                    ) as BreachingEntity[];
                } else {
                    // For other patterns, use raw entities (Pattern A/C already have L1 keys)
                    breachingEntities = rawEntities as BreachingEntity[];
                }
            }

            // =========================================================================
            // NEW: Extract contextFilters from data.filters.a.sourceParams
            // This shows the context/filters used to evaluate this alert
            // =========================================================================
            if (parsedData.filters?.a?.sourceParams) {
                contextFilters = parsedData.filters.a
                    .sourceParams as AlertContextFilters;
            } else if (parsedData.filters?.a) {
                // Sometimes filters.a IS the sourceParams directly
                contextFilters = parsedData.filters.a as AlertContextFilters;
            }

            // The alert's own deep link (context.artifact, copied onto the item by the evaluator).
            if (
                parsedData.artifact &&
                typeof parsedData.artifact === 'object' &&
                typeof (parsedData.artifact as { route?: unknown }).route === 'string'
            ) {
                artifact = parsedData.artifact as AlertArtifactLink;
            }
            // What was done with it since the run (written back by the owning surface).
            if (
                parsedData.outcome &&
                typeof parsedData.outcome === 'object' &&
                typeof (parsedData.outcome as { state?: unknown }).state === 'string' &&
                typeof (parsedData.outcome as { summary?: unknown }).summary === 'string'
            ) {
                outcome = parsedData.outcome as ArtifactOutcome;
            }

            // =========================================================================
            // NEW: Extract rawSupportingData from data.supportingData
            // This contains the pre-computed drill-down data from supportingDataGenerator
            // =========================================================================
            if (
                parsedData.supportingData &&
                typeof parsedData.supportingData === 'object'
            ) {
                rawSupportingData = parsedData.supportingData as Record<
                    string,
                    unknown
                >;
            }

            // Extract supporting data points from conditions (legacy format)
            // Backend format: { data: { conditions: { a: { sourceValue, comparatorValue, ... } } } }
            if (parsedData.data?.conditions) {
                const conditions = parsedData.data.conditions;
                Object.entries(conditions).forEach(
                    ([key, value]: [string, unknown]) => {
                        const condition = value as Record<string, unknown>;
                        if (condition.sourceValue !== undefined) {
                            supportingData.push({
                                key: `${key}_value`,
                                label: 'Affected Items',
                                value: condition.sourceValue as number,
                                format: 'number',
                            });
                        }
                    },
                );
            }

            // Extract priority if present in data
            if (parsedData.priority) {
                priority = parsedData.priority as Priority;
            }
        } catch (e) {
            console.warn('Failed to parse checklist item data:', e);
        }
    }

    // Parse 'actions' JSON string → actions array
    // IMPORTANT: Only show actions from API, no defaults
    const actionsString = rawData[mapping.actions] as string | null;
    if (actionsString) {
        try {
            const parsedActions = JSON.parse(actionsString);
            if (Array.isArray(parsedActions) && parsedActions.length > 0) {
                actions = parsedActions.map((action: Record<string, unknown>) =>
                    mapApiToAction(action, mapping),
                );
            }
            // If parsedActions is empty array, actions stays empty []
        } catch (e) {
            console.warn('Failed to parse checklist item actions:', e);
            // Don't show any actions if parsing fails - keep empty []
        }
    }
    // No 'else' case - if no actions in API, show none

    // =========================================================================
    // MAP STATUS
    // Backend: CREATED, SEEN, COMPLETED, SNOOZED, IGNORED
    // Frontend: pending, in_progress, completed, snoozed, cancelled
    // =========================================================================
    const backendStatus = rawData[mapping.status] as string;
    const frontendStatus =
        BACKEND_STATUS_MAP[backendStatus] || AlertStatus.PENDING;

    // =========================================================================
    // BUILD ALERT ITEM
    // =========================================================================
    // Get generatedBy from the backend response
    const backendGeneratedBy = rawData.generatedBy as string | null;
    const generatedBy = backendGeneratedBy
        ? (backendGeneratedBy as GeneratedBy)
        : undefined;

    return {
        id: rawData[mapping.alertId] as string,
        alertType,
        alertTypeLabel,
        alertLabel: backendAlertLabel || undefined,
        title: rawData[mapping.title] as string,
        description: undefined, // Can extract from data if needed
        priority,
        status: frontendStatus,
        supportingData,
        actions,
        createdAt:
            (rawData[mapping.createdAt] as string) || new Date().toISOString(),
        updatedAt:
            (rawData[mapping.updatedAt] as string) || new Date().toISOString(),
        dueDate: rawData[mapping.dueDate] as string | undefined,
        alertActiveFrom: rawData.alertActiveFrom as string | undefined,
        assignee: undefined,
        source: undefined,
        relatedEntityId: rawData[mapping.relatedEntityId] as string | undefined,
        snoozedUntil: undefined,
        metadata: { raw: rawData }, // Store original data for debugging
        contextFilters,
        breachingEntities,
        artifact,
        outcome,
        rawSupportingData,
        generatedBy,
        entityLinkedFields,
        displayColumns: Array.isArray(rawData.displayColumns)
            ? (rawData.displayColumns as string[])
            : undefined,
    };
};

/**
 * Returns default actions when none are defined in backend
 */
const getDefaultActions = (): AlertAction[] => [
    {
        id: 'view',
        type: ActionType.VIEW_DETAILS,
        label: 'View Details',
        variant: 'secondary',
        requiresConfirmation: false,
    },
    {
        id: 'complete',
        type: ActionType.MARK_COMPLETE,
        label: 'Mark Complete',
        variant: 'success',
        requiresConfirmation: true,
        confirmationMessage: 'Mark this alert as complete?',
    },
];

/**
 * Maps raw API action to AlertAction using field mapping
 */
export const mapApiToAction = (
    rawData: Record<string, unknown>,
    mapping: FieldMapping = DEFAULT_FIELD_MAPPING,
): AlertAction => {
    return {
        id: rawData[mapping.actionId] as string,
        type: rawData[mapping.actionType] as AlertAction['type'],
        label: rawData[mapping.actionLabel] as string,
        variant: rawData[mapping.actionVariant] as AlertAction['variant'],
        requiresConfirmation: rawData[
            mapping.actionRequiresConfirmation
        ] as boolean,
        confirmationMessage: rawData[
            mapping.actionConfirmationMessage
        ] as string,
        isEditable: rawData[mapping.actionIsEditable] as boolean,
        editUrl: rawData[mapping.actionEditUrl] as string,
        apiEndpoint: rawData[mapping.actionApiEndpoint] as string,
    };
};

// =============================================================================
// API: FETCH CHECKLIST ITEMS (ALERTS)
// Endpoint: GET /check-list-items/list
// =============================================================================

/**
 * Fetches all checklist items and categorizes them for the home page
 *
 * FLOW:
 * 1. Get JWT token from localStorage (key: "token")
 * 2. Call GET /api/v1/auth/me to get organizationId (merchant_id)
 * 3. Call GET /api/v1/check-list-items/list with { merchant_id } in body
 *
 * BACKEND ENDPOINT: GET /api/v1/check-list-items/list
 * - Method: GET
 * - Body: { merchant_id: string }
 * - Response: CheckListItemDto[]
 *
 * STATUS MAPPING:
 * - CREATED, SEEN → Pending Alerts tab
 * - COMPLETED → Done Alerts tab
 * - SNOOZED → Snoozed Alerts tab
 * - IGNORED → Done Alerts tab (cancelled)
 *
 * @param generatedBy - Optional filter to show only SYSTEM or USER generated alerts
 * @returns Promise<CategorizedAlerts> - Alerts grouped by status
 */
export const fetchAlertsByCategory = async (
    generatedBy?: GeneratedBy,
): Promise<CategorizedAlerts> => {
    try {
        // =====================================================================
        // STEP 1: Get merchant_id from /auth/me using JWT token
        // =====================================================================
        const merchantId = await getMerchantId();

        if (!merchantId) {
            console.error(
                'Could not resolve merchant_id from auth - returning empty alerts',
            );
            return EMPTY_CATEGORIZED_ALERTS;
        }

        // =====================================================================
        // STEP 2: Fetch checklist items with merchant_id as query parameter
        // GET /check-list-items/list?merchant_id=xxx&generated_by=xxx
        // =====================================================================
        const baseUrl = buildUrl(API_ENDPOINTS.listAlerts);
        let url = `${baseUrl}?merchant_id=${encodeURIComponent(merchantId)}`;

        // Add generated_by filter if specified
        if (generatedBy) {
            url += `&generated_by=${encodeURIComponent(generatedBy)}`;
        }

        console.log(
            '[API] Fetching checklist items for merchant:',
            merchantId,
            'generatedBy:',
            generatedBy,
        );
        console.log('[API] URL:', url);

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            console.error(
                `Failed to fetch checklist items: ${response.status} ${response.statusText}`,
            );
            return EMPTY_CATEGORIZED_ALERTS;
        }

        // Response is array of CheckListItemDto
        const data: Record<string, unknown>[] = await response.json();

        console.log(`[API] Received ${data.length} checklist items`);

        // Map each CheckListItemDto to AlertItem
        const alerts = data.map((raw) => mapApiToAlert(raw));

        // =====================================================================
        // STEP 3: Categorize by status
        // Backend status CREATED/SEEN → pending (actionable)
        // Backend status COMPLETED → done
        // Backend status IGNORED → done (cancelled)
        // Backend status SNOOZED → snoozed
        // =====================================================================
        const pendingAlerts = alerts.filter(
            (a) =>
                a.status === AlertStatus.PENDING ||
                a.status === AlertStatus.IN_PROGRESS,
        );

        const doneAlerts = alerts.filter(
            (a) =>
                a.status === AlertStatus.COMPLETED ||
                a.status === AlertStatus.CANCELLED,
        );

        const snoozedAlerts = alerts.filter(
            (a) => a.status === AlertStatus.SNOOZED,
        );

        // =====================================================================
        // STEP 4: Sort by updatedAt descending (most recent first)
        // =====================================================================
        const sortByUpdatedAt = (a: AlertItem, b: AlertItem) => {
            const dateA = new Date(a.updatedAt || a.createdAt).getTime();
            const dateB = new Date(b.updatedAt || b.createdAt).getTime();
            return dateB - dateA; // Descending (newest first)
        };

        pendingAlerts.sort(sortByUpdatedAt);
        doneAlerts.sort(sortByUpdatedAt);
        snoozedAlerts.sort(sortByUpdatedAt);

        // Debug logging
        console.log(
            `[API] Categorized: ${pendingAlerts.length} pending, ${doneAlerts.length} done, ${snoozedAlerts.length} snoozed`,
        );
        console.log(
            '[API] Pending alert IDs:',
            pendingAlerts.map((a) => ({
                id: a.id,
                status: a.status,
                title: a.title?.substring(0, 30),
            })),
        );

        return {
            pending: pendingAlerts,
            done: doneAlerts,
            snoozed: snoozedAlerts,
        };
    } catch (error) {
        console.error('Error fetching checklist items:', error);
        return EMPTY_CATEGORIZED_ALERTS;
    }
};

// =============================================================================
// CACHE: Store all alerts for lookup by ID
// =============================================================================
let cachedAlerts: AlertItem[] = [];

// =============================================================================
// API: FETCH ALERT BY ID
// Uses cached alerts or fetches fresh data
// =============================================================================

/**
 * Fetches a single alert by its checklist item ID
 * First checks cache, then fetches from API if not found
 *
 * @param alertId - The checklist item ID
 * @returns Promise<AlertItem | null>
 */
export const fetchAlertById = async (
    alertId: string,
): Promise<AlertItem | null> => {
    // Check cache first
    const cached = cachedAlerts.find((a) => a.id === alertId);
    if (cached) {
        return cached;
    }

    // Fetch all alerts and find the one we need
    const categorized = await fetchAlertsByCategory();
    cachedAlerts = [
        ...categorized.pending,
        ...categorized.done,
        ...categorized.snoozed,
    ];

    return cachedAlerts.find((a) => a.id === alertId) || null;
};

// =============================================================================
// API: FETCH PURCHASE ORDER ITEMS
// Endpoint: POST /purchase-orders/items/list
// =============================================================================

export interface PurchaseOrderItemLine {
    productId: string;
    name?: string;
    sku?: string;
    quantity: number;
    dispatchedQuantity: number;
}

export interface PurchaseOrderItemsListResponse {
    purchaseOrderId: string;
    merchantId: string;
    channel?: string;
    vendor?: string;
    warehouseId?: string;
    type: string;
    orderDate: string;
    expiryDate?: string;
    deliveryDate?: string;
    status: string;
    grnIds: string[];
    items: PurchaseOrderItemLine[];
    totalItems?: number;
    page?: number;
    pageSize?: number;
    hasNext?: boolean;
}

export interface SingleWarehouseStockProduct {
    product_id: string;
    product_name: string;
    sku: string;
    quantity: number;
    consumption_per_day?: number;
    consumption_per_month?: number;
    days_on_hand?: number;
    months_on_hand?: number;
}

export interface SingleWarehouseStockResponse {
    data: SingleWarehouseStockProduct[];
    total: number;
    page?: number;
    pageSize?: number;
}

export interface InternalWarehouseGroupingStock {
    quantity: number;
    consumption_per_day?: number;
    consumption_per_month?: number;
    days_on_hand?: number;
    months_on_hand?: number;
    warehouse_name?: string;
}

export interface InternalWarehouseStockProduct {
    product_id: string;
    product_name: string;
    sku: string;
    quantity: number;
    consumption_per_day?: number;
    consumption_per_month?: number;
    days_on_hand?: number;
    months_on_hand?: number;
    groupings: Record<string, InternalWarehouseGroupingStock>;
}

export interface InternalWarehouseStockAggregateResponse {
    data: InternalWarehouseStockProduct[];
    total: number;
    page?: number;
    pageSize?: number;
    inventory_config?: {
        inventory_period: 'DAY' | 'MONTH';
        over_stock_limit?: number;
        under_stock_limit?: number;
    };
}

/**
 * Fetches PO line items by purchaseOrderId.
 * Used in Alert Detail for PO alerts when user clicks a PO row.
 */
export const fetchPurchaseOrderItems = async (
    purchaseOrderId: string,
    options?: { page?: number; pageSize?: number },
): Promise<PurchaseOrderItemsListResponse> => {
    const token = getAuthToken();
    const url = `${API_BASE}/api/v1/purchase-orders/items/list`;

    console.log('[API] Fetching PO items for:', purchaseOrderId);

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
            purchaseOrderId,
            ...(options?.page !== undefined ? { page: options.page } : {}),
            ...(options?.pageSize !== undefined
                ? { pageSize: options.pageSize }
                : {}),
        }),
    });

    if (!response.ok) {
        throw new Error(
            `Failed to fetch purchase order details: ${response.status} ${response.statusText}`,
        );
    }

    const raw = (await response.json()) as unknown;

    if (
        raw &&
        typeof raw === 'object' &&
        'status' in raw &&
        (raw as { status?: unknown }).status === 'ok'
    ) {
        const wrapped = raw as {
            status: string;
            results?: PurchaseOrderItemsListResponse;
        };
        if (!wrapped.results) {
            throw new Error('Purchase order details response was empty');
        }
        return wrapped.results;
    }

    return raw as PurchaseOrderItemsListResponse;
};

/**
 * Fetches stock information for specific products in a given warehouse.
 * Endpoint: POST /warehouse-stock/:warehouseId
 */
export const fetchSingleWarehouseStock = async (
    warehouseId: string,
    productIds: string[],
): Promise<SingleWarehouseStockResponse> => {
    const token = getAuthToken();
    const url = `${API_BASE}/api/v1/warehouse-stock/${encodeURIComponent(warehouseId)}`;

    console.log('[API] Fetching single warehouse stock:', {
        warehouseId,
        productIds,
    });

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
            productIds,
            page: 1,
            pageSize: Math.max(productIds.length, 1),
        }),
    });

    if (!response.ok) {
        throw new Error(
            `Failed to fetch warehouse stock: ${response.status} ${response.statusText}`,
        );
    }

    const raw = (await response.json()) as unknown;

    if (
        raw &&
        typeof raw === 'object' &&
        'status' in raw &&
        (raw as { status?: unknown }).status === 'ok'
    ) {
        const wrapped = raw as {
            status: string;
            results?: SingleWarehouseStockResponse;
        };
        if (!wrapped.results) {
            throw new Error('Warehouse stock response was empty');
        }
        return wrapped.results;
    }

    return raw as SingleWarehouseStockResponse;
};

/**
 * Fetches INTERNAL warehouse split for a product (groupings by warehouse).
 * Endpoint: POST /warehouse-stock with type INTERNAL.
 */
export const fetchInternalWarehouseStockForProduct = async (
    productId: string,
): Promise<InternalWarehouseStockAggregateResponse> => {
    const token = getAuthToken();
    const url = `${API_BASE}/api/v1/warehouse-stock`;

    console.log(
        '[API] Fetching INTERNAL warehouse stock for product:',
        productId,
    );

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
            type: 'INTERNAL',
            productIds: [productId],
            page: 1,
            pageSize: 100,
        }),
    });

    if (!response.ok) {
        throw new Error(
            `Failed to fetch internal warehouse stock: ${response.status} ${response.statusText}`,
        );
    }

    const raw = (await response.json()) as unknown;

    if (
        raw &&
        typeof raw === 'object' &&
        'status' in raw &&
        (raw as { status?: unknown }).status === 'ok'
    ) {
        const wrapped = raw as {
            status: string;
            results?: InternalWarehouseStockAggregateResponse;
        };
        if (!wrapped.results) {
            throw new Error('Internal warehouse stock response was empty');
        }
        return wrapped.results;
    }

    return raw as InternalWarehouseStockAggregateResponse;
};

// =============================================================================
// API: FETCH ACTION DETAILS
// Endpoint: GET /alerts/:alertId/actions/:actionId/details
// =============================================================================

/**
 * Fetches detailed information about an action when user clicks it
 *
 * @param alertId - The alert ID
 * @param actionId - The action ID
 * @returns Promise<ActionDetails> - Detailed action information
 */
export const fetchActionDetails = async (
    alertId: string,
    actionId: string,
): Promise<ActionDetails> => {
    const token = getAuthToken();
    const url = buildUrl(API_ENDPOINTS.getActionDetails, { alertId, actionId });

    console.log('[API] Fetching action details:', url);

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });

    if (!response.ok) {
        throw new Error(
            `Failed to fetch action details: ${response.statusText}`,
        );
    }

    const data: ActionDetailsResponse = await response.json();
    return data.data;
};

// =============================================================================
// API: EXECUTE ACTION
// Endpoint: POST /alerts/:alertId/actions/:actionId/execute
// =============================================================================

/**
 * Executes an action after user confirms
 *
 * API CALL: POST {baseUrl}/alerts/:alertId/actions/:actionId/execute
 *
 * INTEGRATION:
 * - Line 270: Uncomment fetch call
 * - Line 271: Remove dummy data return
 * - Update API_ENDPOINTS.executeAction in types.ts
 *
 * @param alertId - The alert ID
 * @param action - The action to execute
 * @param payload - Additional data (e.g., edited fields from iframe)
 * @returns Promise<ActionExecuteResponse>
 */
export const executeAlertAction = async (
    alertId: string,
    action: AlertAction,
    payload?: Record<string, unknown>,
): Promise<ActionExecuteResponse> => {
    const token = getAuthToken();
    const url = action.apiEndpoint
        ? `${API_BASE}${API_ENDPOINTS.baseUrl}${action.apiEndpoint}`
        : buildUrl(API_ENDPOINTS.executeAction, {
              alertId,
              actionId: action.id,
          });

    console.log('[API] Executing action:', url, payload);

    const response = await fetch(url, {
        method: action.apiMethod || 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ actionId: action.id, ...payload }),
    });

    if (!response.ok) {
        throw new Error(`Failed to execute action: ${response.statusText}`);
    }

    return response.json();
};

// =============================================================================
// API: SNOOZE ALERT
// Endpoint: POST /alerts/:alertId/snooze
// =============================================================================

/**
 * Snoozes an alert for a specified duration
 *
 * API CALL: POST {baseUrl}/alerts/:alertId/snooze
 *
 * INTEGRATION:
 * - Line 330: Uncomment fetch call
 * - Update API_ENDPOINTS.snoozeAlert in types.ts
 *
 * @param alertId - The alert ID
 * @param minutes - Number of minutes to snooze
 * @returns Promise<SnoozeResponse>
 */
export const snoozeAlert = async (
    alertId: string,
    minutes: number,
): Promise<SnoozeResponse> => {
    const token = getAuthToken();
    const url = `${API_BASE}/api/v1/check-list-items/update/${alertId}`;

    // Calculate new alertActiveFrom (now) and alertActiveUntil (now + snooze period)
    const now = new Date();
    const alertActiveFrom = now.toISOString();
    const alertActiveUntil = new Date(
        now.getTime() + minutes * 60 * 1000,
    ).toISOString();

    console.log('[API] Snoozing alert:', alertId, 'for', minutes, 'minutes');
    console.log('[API] New alertActiveFrom:', alertActiveFrom);
    console.log('[API] New alertActiveUntil:', alertActiveUntil);

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
            status: 'SNOOZED',
            alertActiveFrom,
            alertActiveUntil,
        }),
    });

    if (!response.ok) {
        throw new Error(`Failed to snooze alert: ${response.statusText}`);
    }

    const snoozedUntil = new Date(alertActiveUntil).toLocaleDateString(
        'en-IN',
        {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        },
    );

    return {
        success: true,
        message: `Snoozed until ${snoozedUntil}`,
        snoozedUntil: alertActiveUntil,
    };
};

// =============================================================================
// API: DELETE ALERT
// Endpoint: DELETE /alerts/:alertId
// =============================================================================

/**
 * Deletes an alert
 *
 * API CALL: DELETE {baseUrl}/alerts/:alertId
 *
 * INTEGRATION:
 * - Line 380: Uncomment fetch call
 * - Update API_ENDPOINTS.deleteAlert in types.ts
 *
 * @param alertId - The alert ID to delete
 * @returns Promise<DeleteResponse>
 */
export const deleteAlert = async (alertId: string): Promise<DeleteResponse> => {
    const token = getAuthToken();
    const url = buildUrl(API_ENDPOINTS.deleteAlert, { alertId });

    console.log('[API] Deleting alert:', alertId);

    const response = await fetch(url, {
        method: 'DELETE',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
    });

    if (!response.ok) {
        throw new Error(`Failed to delete alert: ${response.statusText}`);
    }

    return response.json();
};

// =============================================================================
// API: IGNORE ALERT
// Endpoint: POST /check-list-items/update/:id
// Changes status to IGNORED
// =============================================================================

/**
 * Ignores an alert (changes status to IGNORED)
 *
 * API CALL: POST {baseUrl}/check-list-items/update/:id
 * Body: { status: 'IGNORED' }
 *
 * @param alertId - The checklist item ID to ignore
 * @returns Promise<{ success: boolean; message: string }>
 */
export const ignoreAlert = async (
    alertId: string,
): Promise<{ success: boolean; message: string }> => {
    const token = getAuthToken();
    const url = `${API_BASE}/api/v1/check-list-items/update/${alertId}`;

    console.log('[API] Ignoring alert:', alertId);

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ status: 'IGNORED' }),
    });

    if (!response.ok) {
        throw new Error(`Failed to ignore alert: ${response.statusText}`);
    }

    return { success: true, message: 'Alert ignored' };
};

// =============================================================================
// API: MARK ALERT AS COMPLETE
// Endpoint: POST /check-list-items/update/:id
// =============================================================================

/**
 * Marks an alert as complete
 * Updates status to COMPLETED in check_list_items table
 *
 * @param alertId - The checklist item ID
 * @returns Promise with success status
 */
export const completeAlert = async (
    alertId: string,
): Promise<{ success: boolean; message: string }> => {
    const token = getAuthToken();
    const url = `${API_BASE}/api/v1/check-list-items/update/${alertId}`;

    console.log('[API] Completing alert:', alertId);

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ status: 'COMPLETED' }),
    });

    if (!response.ok) {
        throw new Error(`Failed to complete alert: ${response.statusText}`);
    }

    return { success: true, message: 'Alert marked as complete' };
};

// =============================================================================
// API: SUBMIT IFRAME EDIT
// Endpoint: PUT /alerts/:alertId (or custom endpoint from action)
// =============================================================================

/**
 * Submits edited data from iframe
 * Called when user confirms changes in the edit iframe
 *
 * API CALL: PUT {baseUrl}/alerts/:alertId (or action.apiEndpoint)
 *
 * INTEGRATION:
 * - Line 430: Uncomment fetch call
 * - Update API_ENDPOINTS.updateAlert in types.ts
 *
 * @param alertId - The alert ID
 * @param action - The action being edited
 * @param editedData - Data edited in the iframe
 * @returns Promise<ActionExecuteResponse>
 */
export const submitIframeEdit = async (
    alertId: string,
    action: AlertAction,
    editedData: Record<string, unknown>,
): Promise<ActionExecuteResponse> => {
    const token = getAuthToken();
    const url = action.apiEndpoint
        ? `${API_BASE}${API_ENDPOINTS.baseUrl}${action.apiEndpoint}`
        : buildUrl(API_ENDPOINTS.updateAlert, { alertId });

    console.log('[API] Submitting iframe edit:', url, editedData);

    const response = await fetch(url, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ actionId: action.id, ...editedData }),
    });

    if (!response.ok) {
        throw new Error(`Failed to submit edit: ${response.statusText}`);
    }

    return response.json();
};

// =============================================================================
// ACTIVITY LOG ENDPOINTS
// =============================================================================

/**
 * Activity Log DTO from backend
 */
export interface ActivityLogDto {
    logId: string;
    checklistItemId: string;
    logDetails: string;
    logType: string | null;
    userName: string | null;
    createdAt: string;
    updatedAt: string;
}

/**
 * Fetch all activity logs for a checklist item
 *
 * API Call: GET /api/v1/check-list-logs/list/:checklistItemId
 *
 * @param checklistItemId - The ID of the checklist item
 * @returns Array of activity logs, ordered by createdAt ASC (oldest first)
 */
export const fetchActivityLogs = async (
    checklistItemId: string,
): Promise<ActivityLogDto[]> => {
    const token = getAuthToken();
    const url = buildUrl(API_ENDPOINTS.getActivityLogs, { checklistItemId });

    console.log('[API] Fetching activity logs for:', checklistItemId);

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });

        if (!response.ok) {
            console.error(
                `Failed to fetch activity logs: ${response.status} ${response.statusText}`,
            );
            return [];
        }

        const logs: ActivityLogDto[] = await response.json();
        console.log(`[API] Fetched ${logs.length} activity logs`);
        return logs;
    } catch (error) {
        console.error('Error fetching activity logs:', error);
        return [];
    }
};

/**
 * Create a new activity log for a checklist item
 *
 * API Call: POST /api/v1/check-list-logs/create
 * Body: { checklistItemId, logDetails, logType, userName }
 *
 * @param checklistItemId - The ID of the checklist item
 * @param logDetails - The log message to display
 * @param logType - Type of log (e.g., 'action_confirmed', 'alert_snoozed')
 * @param userName - Name of the user performing the action
 * @returns The created log entry
 */
export const resolveEntityData = async (
    checklistItemId: string,
    merchantId: string,
    entity: Record<string, unknown>,
): Promise<Record<string, unknown>> => {
    const token = getAuthToken();
    const url = buildUrl(`/check-list-items/resolve-entity/${checklistItemId}`);
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ merchant_id: merchantId, entity }),
        });
        if (!response.ok) return {};
        return (await response.json()) as Record<string, unknown>;
    } catch {
        return {};
    }
};

export const createActivityLog = async (
    checklistItemId: string,
    logDetails: string,
    logType: string,
    userName: string,
): Promise<ActivityLogDto | null> => {
    const token = getAuthToken();
    const url = buildUrl(API_ENDPOINTS.createActivityLog);

    console.log('[API] Creating activity log:', {
        checklistItemId,
        logDetails,
        logType,
        userName,
    });

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
                checklistItemId,
                logDetails,
                logType,
                userName,
            }),
        });

        if (!response.ok) {
            console.error(
                `Failed to create activity log: ${response.status} ${response.statusText}`,
            );
            return null;
        }

        const log: ActivityLogDto = await response.json();
        console.log('[API] Created activity log:', log.logId);
        return log;
    } catch (error) {
        console.error('Error creating activity log:', error);
        return null;
    }
};
